import { highRelativeAbundanceLimit } from 'assets/constants';
import {
  Card,
  ContainerSizes,
  OrderKitButton,
  PageContainer,
  Spinfinity,
  THField,
  Typography,
} from 'components';
import { TaxonomyCategoryFilters } from 'components/TaxonomyCategoryFilters';
import { capitalize } from 'helpers';
import { Suspense, useEffect, useMemo } from 'react';
import useTaxaBarChartData from 'services/microbiome/useTaxaBarChartData';
import { InsightsTaxaList } from 'views/Results/Insights';

import { KitType } from '@repo/types';

import CategoryFilters from '../../../../../components/CategoryFilters/CategoryFilters';
import { MicrobiomeBarList } from '../../MicrobiomeBarList';
import styles from './MicrobiomeBreakdownCard.module.css';
import useParseTaxaData from './hooks/useParseTaxaData';

const categoryColors: { [key: string]: string } = {
  beneficial: 'green',
  protective: 'green',
  unfriendly: 'red',
  disruptive: 'red',
  variable: 'orange',
  unknown: 'grey',
};

const categoryOrders: { [key: string]: number } = {
  unfriendly: 1,
  disruptive: 1,
  beneficial: 2,
  protective: 2,
  variable: 3,
  unknown: 4,
};

const MicrobiomeBreakdownCard = () => {
  const {
    filteredData,
    dataAfterSearchAndTaxonomyFilters,
    setSearchTerm,
    categoryFilters,
    setCategoryFilters,
    taxonomyFilters,
    setTaxonomyFilters,
    currentKit,
  } = useParseTaxaData();

  const currentKitId = currentKit?.id;
  const currentKitType = currentKit?.sampling_type;

  const { data: taxaBarChartData } = useTaxaBarChartData(
    currentKitId!,
    currentKit?.sampling_type ?? KitType.STOOL,
  );

  useEffect(() => {
    if (taxaBarChartData) {
      const allCategories = Object.keys(taxaBarChartData)
        .filter(
          key =>
            taxaBarChartData[key as keyof typeof taxaBarChartData] !== null &&
            !isNaN(
              Number(taxaBarChartData[key as keyof typeof taxaBarChartData]),
            ),
        )
        .map(capitalize);
      setCategoryFilters(allCategories);
    }
  }, [taxaBarChartData]);

  const displayableBacteriaCategories = useMemo(() => {
    if (!taxaBarChartData) return [];

    const categories: Record<
      string,
      { key: string; name: string; color: string; value: number; order: number }
    > = {};

    Object.keys(taxaBarChartData).forEach(categoryKeyLowercase => {
      const rawValue =
        taxaBarChartData[categoryKeyLowercase as keyof typeof taxaBarChartData];
      const numericValue = Number(rawValue);

      if (rawValue !== null && !isNaN(numericValue)) {
        const categoryKeyCapitalized = capitalize(categoryKeyLowercase);

        const color = categoryColors[categoryKeyLowercase] || 'grey';
        const order = categoryOrders[categoryKeyLowercase] || 99;
        const percentage = numericValue;

        categories[categoryKeyCapitalized] = {
          key: categoryKeyCapitalized,
          name: categoryKeyCapitalized,
          color,
          value: percentage,
          order,
        };
      }
    });
    return Object.values(categories).sort((a, b) => a.order - b.order);
  }, [taxaBarChartData]);

  const handleCategoryFilterClick = (categoryKey: string) => {
    setCategoryFilters(prev =>
      prev.includes(categoryKey)
        ? prev.filter(c => c !== categoryKey)
        : [...prev, categoryKey],
    );
  };

  const hiddenMetricsCount = useMemo(() => {
    if (
      !dataAfterSearchAndTaxonomyFilters ||
      dataAfterSearchAndTaxonomyFilters.length === 0
    ) {
      return 0;
    }

    const count = dataAfterSearchAndTaxonomyFilters.filter(bacterium => {
      // Determine which category field to use based on kit type
      const categoryValue =
        currentKitType === KitType.VAGINAL
          ? bacterium.category_vaginal
          : bacterium.category;

      let categoryToTest: string;
      if (
        categoryValue === null ||
        categoryValue === undefined ||
        categoryValue.trim() === ''
      ) {
        categoryToTest = 'Unknown';
      } else {
        // Assumes categoryValue is string here
        categoryToTest = capitalize(categoryValue);
      }

      const isHiddenByCategory = !categoryFilters.includes(categoryToTest);
      return isHiddenByCategory;
    }).length;

    return count;
  }, [dataAfterSearchAndTaxonomyFilters, categoryFilters, currentKitType]);

  const handleClearFilters = () => {
    if (taxaBarChartData) {
      const allCategories = Object.keys(taxaBarChartData)
        .filter(
          key =>
            taxaBarChartData[key as keyof typeof taxaBarChartData] !== null &&
            !isNaN(
              Number(taxaBarChartData[key as keyof typeof taxaBarChartData]),
            ),
        )
        .map(capitalize);
      setCategoryFilters(allCategories);
    }
  };

  if (!currentKitId || !currentKit) {
    return (
      <PageContainer size={ContainerSizes.MD}>
        <Typography variant='body-m'>
          You have not taken any gut samples yet or your results are not yet
          ready.
        </Typography>
        <OrderKitButton>Order a Tiny&nbsp;Gut&nbsp;Kit</OrderKitButton>
      </PageContainer>
    );
  }

  return (
    <Card variant='wrapper' className={styles.card}>
      <Suspense fallback={<Spinfinity />}>
        <Typography variant='heading-m' className='mb-4'>
          Microbiome Breakdown
        </Typography>
        <section id='breakdown'>
          <div className='mb-4'>
            This shows what was detected in the sample, but is not meant to
            provide an evaluation if your levels are optimal. For evaluations
            based on your age range, see the All Metrics section.
          </div>
          <div className='pb-3'>
            <div className='mb-3'>
              <CategoryFilters
                displayableBacteriaCategories={displayableBacteriaCategories}
                categoryFilters={categoryFilters}
                handleCategoryFilterClick={handleCategoryFilterClick}
                hiddenMetricsCount={hiddenMetricsCount}
                handleClearFilters={handleClearFilters}
              />
            </div>
            <Suspense key={currentKit.id} fallback={<Spinfinity />}>
              <MicrobiomeBarList />
            </Suspense>
          </div>

          <div className='flex flex-column gap-2'>
            <div className='flex-between align-items-center items-center'>
              <Typography variant='heading-m'>Microbes in sample</Typography>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  const keyword = formData.get('bacteriaSearch');
                  setSearchTerm(keyword as string);
                }}
                className='hide-on-mobile'
              >
                <THField
                  name='bacteriaSearch'
                  label=''
                  onChange={async e => {
                    setSearchTerm(e.target.value);
                  }}
                  onBlur={async () => {}}
                  type='text'
                  icon='search'
                  placeholder='Search bacteria...'
                  classNames={styles.search}
                  hideErrors
                />
              </form>
            </div>

            <TaxonomyCategoryFilters
              filters={taxonomyFilters}
              setFilters={setTaxonomyFilters}
            />

            {filteredData && (
              <InsightsTaxaList
                data={filteredData}
                type={currentKitType}
                taxonomyFilters={taxonomyFilters}
              />
            )}

            <Typography variant='legal' className='mt-2'>
              Our high confidence detection limit is{' '}
              {highRelativeAbundanceLimit * 100}% abundance. Very low abundance
              organisms can be important members of the microbiome but also very
              difficult to accurately detect. Detections below{' '}
              {highRelativeAbundanceLimit * 100}% abundance have an increased
              risk of being false positives. However, in our testing, we have
              observed that most false positive identifications are closely
              related to a detected species.
            </Typography>
          </div>
        </section>
      </Suspense>
    </Card>
  );
};

export default MicrobiomeBreakdownCard;
